import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import OkonomiTilbudPage from "./OkonomiTilbudPage";
import FakturaPage from "./FakturaPage";
import BilagPage from "./BilagPage";
import MersalgPage from "./MersalgPage";
import StatisterPage from "./StatisterPage";
import RapportPage from "./RapportPage";
import ProvisjonTab from "@/components/project/ProvisjonTab";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { SrIcon, type SrIconName } from "@/components/ui/SrIcon";

type Section = "tilbud" | "faktura" | "bilag" | "mersalg" | "statister" | "rapport" | "provisjon";

const SECTIONS: Array<{ key: Section; label: string; icon: SrIconName }> = [
  { key: "tilbud", label: "Tilbud", icon: "money" },
  { key: "faktura", label: "Faktura", icon: "file-text" },
  { key: "bilag", label: "Bilag", icon: "receipt" },
  { key: "mersalg", label: "Mersalg", icon: "trending-up" },
  { key: "statister", label: "Statister / Ekstern", icon: "users" },
  // VIB-947-oppfølging: provisjonssplitt-redigeringen ble fjernet fra Detaljer
  // og lå hjemløs. Her er den «egen, tilgangsstyrte» plassen. VIB-1165: se/endre-
  // split — synlig med view_commission (Se), redigerbar med manage_commission_split.
  { key: "provisjon", label: "Provisjon", icon: "percent" },
  { key: "rapport", label: "Rapport", icon: "bar-chart3" },
];

/**
 * Consolidated economy view — groups Faktura, Bilag, Mersalg and Statister
 * under one tab with pill-based sub-navigation.
 * URL: /prosjekter/:id/okonomi?seksjon=faktura|bilag|mersalg|statister
 *
 * Each underlying *Page component is kept intact for easy rollback to
 * separate tabs if this consolidation doesn't work out.
 */
export default function EconomyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { project, owners } = useProjectDetailContext();
  // VIB-1165: Se/Endre-split. Se Provisjon = view_commission (impliseres av
  // manage_commission_split), redigere = manage_commission_split.
  const canViewCommission = useProjectPermission(project.id, "view_commission");
  const canManageCommission = useProjectPermission(project.id, "manage_commission_split");
  const rawSection = (searchParams.get("seksjon") as Section) ?? "tilbud";
  // Provisjon er gated på Se; faller tilbake til Tilbud uten leserett (også ved
  // direkte URL til ?seksjon=provisjon).
  const section: Section = rawSection === "provisjon" && !canViewCommission ? "tilbud" : rawSection;
  const sections = SECTIONS.filter((s) => s.key !== "provisjon" || canViewCommission);

  const setSection = (s: Section) => {
    setSearchParams(
      (prev) => {
        if (s === "tilbud") prev.delete("seksjon");
        else prev.set("seksjon", s);
        return prev;
      },
      { replace: true }
    );
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        {/* Pill sub-navigation */}
        <div className="flex gap-1 bg-muted/50 rounded-lg p-1 overflow-x-auto">
          {sections.map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setSection(key)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
                section === key
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <SrIcon name={icon} className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Active section */}
        {section === "tilbud" && <OkonomiTilbudPage />}
        {section === "faktura" && <FakturaPage />}
        {section === "bilag" && <BilagPage />}
        {section === "mersalg" && <MersalgPage />}
        {section === "statister" && <StatisterPage />}
        {section === "provisjon" && canViewCommission && (
          <ProvisjonTab
            projectId={project.id}
            owners={owners}
            reellBf={project.bf_actual ?? null}
            commissionLockedAt={project.commission_locked_at}
            commissionRatePct={project.commission_rate_pct}
            readOnly={!canManageCommission}
          />
        )}
        {section === "rapport" && <RapportPage />}
      </div>
    </ErrorBoundary>
  );
}
